// The Events tab of the guide dock: detected events newest first as cards. A card shows only what the event carries —
// its interpretation verbatim and its evidence rows ("what we used") — plus the cause / effect links the detector drew,
// and a "Show me" that opens the lens on the event and highlights it on the trend. Nothing here is derived or invented.
import { LANE_BY_CLASS, clock } from "./detect.js"
import { SEVERITY_SOLID, CLASS_LABELS, classLabel } from "./chart-events.js"

export { CLASS_LABELS, classLabel }

const SEV_RANK = { info: 0, warning: 1, alarm: 2 }
const UNITS = ["ST-101", "ST-102", "ST-103", "ST-104", "ST-105", "ST-106", "TK-200", "LINE-300"]

/** The one "show me" recipe for an event (tour chips and the Events tab share it): lane, window, emphasised unit. */
export function eventFocus(ev, now) {
  const at = Number.isFinite(now) ? now : ev.t1 ?? ev.t0
  const lane = LANE_BY_CLASS[ev.class] || "wt"
  // Station lanes stay readable at <= 15 min; the tank lane and long wear events may open wider (up to the 8 h shift).
  const wide = lane === "tank" || ev.class === "wear.projection"
  const span = Math.max(300, (at - ev.t0) * 1.25)
  return { target: `lane:${lane}`, window: Math.round(Math.min(wide ? 28800 : 900, span)), emphasize: ev.unit || null }
}

/** One clock for a whole card: the absolute plant time the interpretation itself uses. A single time when start == end. */
const span = (a, b) => (b == null || Math.round(b) === Math.round(a) ? clock(a) : `${clock(a)} to ${clock(b)}`)
const idNum = (id) => Number(String(id).replace(/\D/g, "")) || 0
const fmtVal = (v) => (Number.isFinite(v) ? (Math.abs(v) >= 100 ? v.toFixed(0) : String(+v.toFixed(2))) : "")

export function createEventsView(root, { detector, lens, trend, now: nowFn, doc: docIn } = {}) {
  const doc = docIn || root.ownerDocument || document
  const h = (tag, attrs = {}, text) => {
    const e = doc.createElement(tag)
    for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v)
    if (text != null) e.textContent = text
    return e
  }
  const filter = { sev: "all", unit: "all" }
  const cards = new Map()          // id -> { art, head, ... , sig }
  let openId = null, unseenN = 0, destroyed = false
  const newFns = new Set()
  const events = () => (detector && detector.events) || []
  const now = () => {
    if (nowFn) { const n = nowFn(); if (Number.isFinite(n)) return n }
    let m = 0; for (const e of events()) m = Math.max(m, e.t1 ?? e.t0); return m
  }

  // ---- skeleton
  const style = h("style")
  style.textContent = Object.entries(SEVERITY_SOLID).map(([k, col]) => `.ev-card[data-severity="${k}"]{border-left-color:${col}}`).join("\n")
  const bar = h("div", { class: "ev-filters", role: "group", "aria-label": "Filter events" })
  const sevRow = h("div", { class: "ev-chips" }), unitRow = h("div", { class: "ev-chips" })
  const chip = (row, group, value, label) => {
    const b = h("button", { type: "button", class: "chip ev-chip", "data-group": group, "data-value": value, "aria-pressed": "false" }, label)
    b.addEventListener("click", () => { filter[group] = value; render() })
    row.appendChild(b); return b
  }
  const chips = []
  for (const [v, l] of [["all", "All"], ["warning", "Warning+"], ["alarm", "Alarm"]]) chips.push(chip(sevRow, "sev", v, l))
  for (const [v, l] of [["all", "All units"], ...UNITS.map((u) => [u, u])]) chips.push(chip(unitRow, "unit", v, l))
  const count = h("p", { class: "ev-count", "aria-live": "polite" })
  const list = h("div", { class: "ev-list" })
  const empty = h("p", { class: "ev-empty" }, "Detected events will appear here.")
  bar.append(sevRow, unitRow)
  root.append(style, bar, count, list, empty)

  const set = (el, prop, val) => { if (el[prop] !== val) el[prop] = val }
  const setAttr = (el, k, v) => { if (el.getAttribute(k) !== v) el.setAttribute(k, v) }

  function makeCard(ev) {
    const art = h("article", { class: "ev-card", "data-id": ev.id })
    const head = h("button", { type: "button", class: "ev-head", "aria-expanded": "false", "aria-controls": `ev-body-${ev.id}` })
    const title = h("span", { class: "ev-title" }), unit = h("span", { class: "ev-unit" }), time = h("span", { class: "ev-time" }), live = h("span", { class: "ev-live" })
    head.append(title, unit, time, live)
    const body = h("div", { class: "ev-body", id: `ev-body-${ev.id}` }); body.hidden = true
    art.append(head, body)
    head.addEventListener("click", () => (openId === ev.id ? collapse() : open(ev.id)))
    return { art, head, title, unit, time, live, body, sig: "" }
  }

  function fillBody(c, ev) {
    const sig = JSON.stringify([ev.interpretation, ev.evidence, ev.causes, ev.effects])
    if (sig === c.sig) return
    c.sig = sig
    c.body.textContent = ""
    c.body.appendChild(h("p", { class: "ev-interp" }, ev.interpretation))
    if (ev.evidence && ev.evidence.length) {
      c.body.appendChild(h("h4", { class: "ev-h" }, "What we used"))
      const ul = h("ul", { class: "ev-evidence" })
      for (const r of ev.evidence) {
        const li = h("li", { class: "ev-row" })
        li.append(h("span", { class: "ev-row-label" }, r.label), h("span", { class: "ev-row-val" }, `${fmtVal(r.value)}${r.unit ? " " + r.unit : ""}`),
          h("span", { class: "ev-row-win" }, `${r.signal} · ${span(r.window[0], r.window[1])}`))
        ul.appendChild(li)
      }
      c.body.appendChild(ul)
    }
    for (const [key, heading] of [["causes", "Caused by"], ["effects", "Led to"]]) {
      const ids = (ev[key] || []).map((id) => events().find((e) => e.id === id)).filter(Boolean)
      if (!ids.length) continue
      const row = h("div", { class: "ev-chain", "data-chain": key })
      row.appendChild(h("span", { class: "ev-chain-h" }, heading))
      for (const o of ids) {
        const b = h("button", { type: "button", class: "ev-link", "data-link": o.id }, `${classLabel(o)}${o.unit ? " · " + o.unit : ""} · ${clock(o.t0)}`)
        b.addEventListener("click", () => open(o.id, { focus: true }))
        row.appendChild(b)
      }
      c.body.appendChild(row)
    }
    const show = h("button", { type: "button", class: "btn ev-show" }, "Show me")
    show.addEventListener("click", () => showMe(ev))
    c.body.appendChild(show)
  }

  function showMe(ev) {
    if (lens && lens.show) lens.show(eventFocus(ev, now()))
    if (trend && typeof trend.highlightEvent === "function") trend.highlightEvent(ev.id)
  }

  function passes(ev) {
    if (filter.sev !== "all" && (SEV_RANK[ev.severity] ?? 0) < SEV_RANK[filter.sev]) return false
    return filter.unit === "all" || ev.unit === filter.unit
  }

  function render() {
    if (destroyed) return
    const all = events()
    const shown = all.filter(passes).sort((a, b) => b.t0 - a.t0 || idNum(b.id) - idNum(a.id))
    const keep = new Set()
    let prev = null
    for (const ev of shown) {
      keep.add(ev.id)
      let c = cards.get(ev.id)
      if (!c) { c = makeCard(ev); cards.set(ev.id, c) }
      const isOpen = openId === ev.id
      set(c.title, "textContent", classLabel(ev)); set(c.unit, "textContent", ev.unit || "")
      set(c.time, "textContent", ev.open || ev.t1 == null ? clock(ev.t0) : span(ev.t0, ev.t1))
      set(c.live, "textContent", ev.open ? "ongoing" : "")
      setAttr(c.art, "data-severity", ev.severity)
      c.art.classList.toggle("ev-compact", ev.class === "batch.complete" && !isOpen)
      setAttr(c.head, "aria-expanded", isOpen ? "true" : "false")
      if (c.body.hidden === isOpen) c.body.hidden = !isOpen
      if (isOpen) fillBody(c, ev)
      const want = prev ? prev.nextSibling : list.firstChild
      if (want !== c.art) list.insertBefore(c.art, want)
      prev = c.art
    }
    for (const [id, c] of cards) if (!keep.has(id)) { c.art.remove(); cards.delete(id); if (openId === id) openId = null }
    const ongoing = shown.filter((e) => e.open).length
    set(count, "textContent", `${shown.length} event${shown.length === 1 ? "" : "s"} · ${ongoing} ongoing`)
    if (empty.hidden !== shown.length > 0) empty.hidden = shown.length > 0
    for (const b of chips) setAttr(b, "aria-pressed", filter[b.dataset.group] === b.dataset.value ? "true" : "false")
  }

  const openFns = new Set()
  function collapse() { openId = null; render() }

  function open(id, { focus = false } = {}) {
    const ev = events().find((e) => e.id === id)
    if (!ev) return
    if (!passes(ev)) { filter.sev = "all"; filter.unit = "all" }   // a chain link must never lead to a hidden card
    openId = id
    render()
    for (const fn of openFns) fn(ev)   // the chat cues hear which events a visitor digs into
    const c = cards.get(id)
    if (c) {
      const calm = doc.defaultView && doc.defaultView.matchMedia && doc.defaultView.matchMedia("(prefers-reduced-motion: reduce)").matches
      if (c.art.scrollIntoView) c.art.scrollIntoView({ block: "nearest", behavior: calm ? "auto" : "smooth" })
      if (focus) c.head.focus({ preventScroll: true })
    }
  }

  const off = detector && detector.onDetect ? detector.onDetect((ev, info) => {
    if (destroyed) return
    if (!(info && info.updated)) { unseenN++; for (const fn of newFns) fn(ev) }
    render()
  }) : null
  render()

  return {
    render, open,
    onNew(fn) { newFns.add(fn); return () => newFns.delete(fn) },
    onOpen(fn) { openFns.add(fn); return () => openFns.delete(fn) },
    unseen: () => unseenN,
    markSeen() { unseenN = 0 },
    destroy() { destroyed = true; if (typeof off === "function") off(); newFns.clear(); openFns.clear(); for (const e of [style, bar, count, list, empty]) e.remove(); cards.clear() },
  }
}
